import { ToolPointer } from './tool';
import { PlaneToolBase } from './planeToolBase';
import { add, sub, dot, mul, vec, Vec3 } from '../core/vec';
import { formatLength, parseMeasureList } from '../core/units';

/** Rechteck-Werkzeug (R): zwei diagonal gegenüberliegende Ecken, achsparallel zur Zeichenebene. */
export class RectangleTool extends PlaneToolBase {
  readonly id = 'rectangle';
  readonly label = 'Rechteck';
  readonly shortcut = 'R';
  readonly cursor = 'crosshair';
  readonly hint = 'Diagonalecke ziehen · Zahl tippen: Länge;Breite · Auf einer Fläche zeichnen teilt diese';

  private corner1: Vec3 | null = null;
  private current: Vec3 | null = null;
  /** Bildschirmposition beim Setzen der ersten Ecke (für Ziehen-und-Loslassen). */
  private pressAt: { x: number; y: number } | null = null;

  enter(): void {
    this.corner1 = null;
    this.current = null;
    this.resetPlane();
  }

  exit(): void {
    this.ctx.preview.clear();
    this.corner1 = null;
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    const snap = this.getSnap(e);
    this.showSnapDot(snap);
    if (!this.corner1) {
      this.current = snap.point;
      this.ctx.requestUpdate();
      return;
    }
    this.pickPlane(this.corner1, e);
    this.current = this.snapInPlane(e, snap, [this.corner1]);
    this.showInference();
    this.drawPreview();
    const size = this.sizeOf();
    this.ctx.ui.setStatus(
      `Fläche: ${formatLength(size[0], this.ctx.units())} × ${formatLength(size[1], this.ctx.units())} · Zahl tippen: Länge;Breite`
    );
    this.ctx.requestUpdate();
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e); // Frischen Snap für die aktuelle Position holen
    if (!this.corner1) {
      const snap = this.snap!;
      this.corner1 = snap.point;
      this.adoptPlane(snap);
      this.pressAt = { x: e.x, y: e.y };
      this.ctx.ui.setStatus('Diagonalecke auswählen · Zahl tippen: Länge;Breite');
    } else {
      this.finish();
    }
    this.ctx.requestUpdate();
  }

  /** Ziehen und Loslassen (statt zweitem Klick) setzt die Diagonalecke — beides wie SketchUp. */
  pointerUp(e: ToolPointer): void {
    const press = this.pressAt;
    this.pressAt = null;
    if (!press || !this.corner1 || Math.hypot(e.x - press.x, e.y - press.y) < 6) return;
    this.pointerMove(e);
    this.finish();
    this.ctx.requestUpdate();
  }

  private finish(): void {
    if (!this.corner1) return;
    const end = this.current ?? this.snap!.point;
    const [a, b] = this.sizeOf(end);
    if (a < 1e-6 || b < 1e-6) return;
    this.commitShape(this.rectPoints(this.corner1, end));
    this.corner1 = null;
    this.current = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
  }

  private rectPoints(c1: Vec3, c2: Vec3): Vec3[] {
    const f = this.frame();
    const d = sub(c2, c1);
    const x = dot(d, f.u);
    const y = dot(d, f.v);
    const p = (dx: number, dy: number): Vec3 => add(c1, add(mul(f.u, dx), mul(f.v, dy)));
    return [p(0, 0), p(x, 0), p(x, y), p(0, y)];
  }

  private drawPreview(): void {
    if (!this.corner1 || !this.current) return;
    const pts = this.rectPoints(this.corner1, this.current);
    this.ctx.preview.polyline(pts, 0x333333, true);
    this.ctx.preview.faceOverlay(pts, 0xdfe7ee);
  }

  private sizeOf(end: Vec3 = this.current!): [number, number] {
    const f = this.frame();
    const d = sub(end, this.corner1!);
    return [Math.abs(dot(d, f.u)), Math.abs(dot(d, f.v))];
  }

  wantsVcb(): boolean {
    return this.corner1 !== null;
  }

  vcbDisplay(): string | null {
    if (!this.corner1 || !this.current) return null;
    const [a, b] = this.sizeOf();
    return `${formatLength(a, this.ctx.units())}; ${formatLength(b, this.ctx.units())}`;
  }

  applyVcb(raw: string): void {
    if (!this.corner1) return;
    const values = parseMeasureList(raw, 'length', this.ctx.units());
    if (!values || values.length === 0) return;
    const f = this.frame();
    // Richtung aus der aktuellen Mausposition (wie SketchUp); negative Werte kehren sie um
    const d = this.current ? sub(this.current, this.corner1) : vec(1, 1, 1);
    const su = dot(d, f.u) < 0 ? -1 : 1;
    const sv = dot(d, f.v) < 0 ? -1 : 1;
    const dx = su * values[0]!;
    const dy = sv * (values.length > 1 ? values[1]! : values[0]!);
    const end = add(this.corner1, add(mul(f.u, dx), mul(f.v, dy)));
    this.commitShape(this.rectPoints(this.corner1, end));
    this.corner1 = null;
    this.current = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  cancel(): void {
    this.pressAt = null;
    this.corner1 = null;
    this.current = null;
    this.resetPlane();
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
